/* eslint-disable eqeqeq */
/*
 * 1-Minute Maths
 * Copyright © 2021 - 2024 White Rose Maths
 * SubitisingGenerator.js -
 */

import { SubitisingCategoryName } from '../../categories';
import { dice_formations } from './DiceFormations';
import { perceptual_formations } from './PercptualFormations';
import { colour_formations } from './ColourFormations';
import { five_frame_formations } from './FiveFrameFormations';
import { ten_frame_formations } from './TenFrameFormations';
import { colourArray, numColours } from '../../colours';
import { subitisingImages, subitisingImageChoices } from './SubitisingImages';

const TIMEOUT = 3000;

export default class SubitisingGenerator {
  constructor() {
    // TODO Do we need to track what
    // the previous questions have been?
    this.lastQuestion = null;
    this.previousQuestions = {};
    this.initPreviousQuestions();
  }

  initPreviousQuestions() {
    this.previousQuestions[SubitisingCategoryName.DICE_FORMATION_1] = [];
    this.previousQuestions[SubitisingCategoryName.RANDOM_FORMATION_1] = [];
  }

  /**
   * Generate a question & answer pair of a specific category
   * @param {Catergory Question} questionType The category of question to generate
   * @returns {question, answer}
   */
  generateQuestion(questionType, lastQuestions) {

    let questionAnswer = null;
    let unique = false;
    while (!unique) {
      switch (questionType) {

        /* Subitising Questions */
        case SubitisingCategoryName.DICE_FORMATION_1:
        {
          questionAnswer = this.diceFormation2(this.previousQuestions, lastQuestions);
          break;
        }

        case SubitisingCategoryName.PERCEPTUAL_FORMATION_1:
        {
          questionAnswer = this.perceptualFormation2(this.previousQuestions, lastQuestions);
          break;
        }

        case SubitisingCategoryName.RANDOM_FORMATION_1:
        {
          questionAnswer = this.randomFormation2(this.previousQuestions, lastQuestions);
          break;
        }
        case SubitisingCategoryName.TWO_COLOURS_1:
        {
          questionAnswer = this.twoColoursFormation2(this.previousQuestions, lastQuestions);
          break;
        }
        case SubitisingCategoryName.OBJECT_FORMATION_1:
        {
          questionAnswer = this.objectFormation2(this.previousQuestions, lastQuestions);
          break;
        }
        case SubitisingCategoryName.FIVE_FRAME_1:
        {
          questionAnswer = this.fiveFrame2(this.previousQuestions, lastQuestions);
          break;
        }
        case SubitisingCategoryName.TEN_FRAME_1:
        {
          questionAnswer = this.tenFrame2(this.previousQuestions, lastQuestions);
          break;
        }
        case SubitisingCategoryName.FIVE_TO_NINE_1:
        {
          questionAnswer = this.fiveToNine2(this.previousQuestions, lastQuestions);
          break;
        }
        case SubitisingCategoryName.ONE_TO_NINE_1:
        {
          questionAnswer = this.oneToNine2(this.previousQuestions, lastQuestions);
          break;
        }
        case SubitisingCategoryName.MAGIC_MIX_SUBITISING_1:
        {
          questionAnswer = this.magicMix2(this.previousQuestions, lastQuestions);
          break;
        }

        default:
        {
          // eslint-disable-next-line no-console
          console.warn('Question type not recognized:', questionType);
          // Generate a random question
          questionAnswer = this.diceFormation1(this.previousQuestions, lastQuestions);
          break;
        }
      }

      if (this.lastQuestion != null) {
        unique = this.checkUnique(questionAnswer, this.lastQuestion);
      } else {
        unique = true;
      }

    }

    this.lastQuestion = questionAnswer;
    return questionAnswer;
  }

  checkUnique(qa, last) {
    if (JSON.stringify(qa) === JSON.stringify(last)) {
      return false;
    }
    return true;
  }

  diceFormation1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    const diceFormation = dice_formations[answer];

    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: diceFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.DICE_FORMATION_1
    };
  }

  diceFormation2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    const diceFormation = dice_formations[answer];

    // TODO random generate colour

    // Generate timeout amount
    let timeout = TIMEOUT;

    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    const questionAnswer = {
      answer, formation: diceFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.DICE_FORMATION_1
    };

    // Generate the string to compare last questions
    const questionAnswerString = JSON.stringify(questionAnswer);

    previousQuestions[SubitisingCategoryName.DICE_FORMATION_1].push(questionAnswerString);

    return questionAnswer;
  }

  perceptualFormation1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    // Pick a random perceptual formation for that number
    const perceptualFormations = perceptual_formations[answer];
    const pLen = perceptualFormations.length;
    // Generate random number between 1 and 5
    const pChoice = Math.floor(Math.random() * pLen);
    const perceptualFormation = perceptualFormations[pChoice];

    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: perceptualFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.PERCEPTUAL_FORMATION_1
    };
  }

  perceptualFormation2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    // Pick a random perceptual formation for that number
    const perceptualFormations = perceptual_formations[answer];
    const pLen = perceptualFormations.length;
    // Generate random number between 1 and 5
    const pChoice = Math.floor(Math.random() * pLen);
    const perceptualFormation = perceptualFormations[pChoice];

    let timeout = TIMEOUT;
    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: perceptualFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.PERCEPTUAL_FORMATION_1
    };
  }

  randomFormation1(previousQuestions, lastQuestions) {

    // Generate random number between 1 and 5
    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    const randomFormation = new Array(5);

    for (let i = 0; i < 5; i++) {
      randomFormation[i] = new Array(5).fill(0);
    }

    let count = answer;

    while (count > 0) {
      // Gen random position in array 0-4
      const i = Math.floor(Math.random() * 5);
      const j = Math.floor(Math.random() * 5);

      if (randomFormation[i][j] != 1) {
        randomFormation[i][j] = 1;
        count -= 1;
      }
    }

    // TODO random generate colour
    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: randomFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.RANDOM_FORMATION_1
    };
  }

  randomFormation2(previousQuestions, lastQuestions) {

    // Generate random number between 1 and 5
    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    const randomFormation = new Array(5);

    for (let i = 0; i < 5; i++) {
      randomFormation[i] = new Array(5).fill(0);
    }

    let count = answer;

    while (count > 0) {
      // Gen random position in array 0-4
      const i = Math.floor(Math.random() * 5);
      const j = Math.floor(Math.random() * 5);

      if (randomFormation[i][j] != 1) {
        randomFormation[i][j] = 1;
        count -= 1;
      }
    }

    // TODO random generate colour
    let timeout = TIMEOUT;

    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: randomFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.RANDOM_FORMATION_1
    };
  }

  twoColoursFormation1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    // Pick a random perceptual formation for that number
    const colourFormations = colour_formations[answer];
    const cLen = colourFormations.length;
    // Generate random number between 1 and 5
    const cChoice = Math.floor(Math.random() * cLen);
    const colourFormation = colourFormations[cChoice];

    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber2];

    return {
      answer, formation: colourFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.TWO_COLOURS_1
    };
  }

  twoColoursFormation2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    // Pick a random perceptual formation for that number
    const colourFormations = colour_formations[answer];
    const cLen = colourFormations.length;
    // Generate random number between 1 and 5
    const cChoice = Math.floor(Math.random() * cLen);
    const colourFormation = colourFormations[cChoice];

    let timeout = TIMEOUT;
    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber2];

    return {
      answer, formation: colourFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.TWO_COLOURS_1
    };
  }

  objectFormation1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    // Pick a random perceptual formation for that number
    const perceptualFormations = perceptual_formations[answer];
    const pLen = perceptualFormations.length;
    // Generate random number between 1 and 5
    const pChoice = Math.floor(Math.random() * pLen);
    const perceptualFormation = perceptualFormations[pChoice];

    const timeout = 0;

    // Pick random object
    const objectNumber = Math.floor(Math.random() * subitisingImageChoices.length);
    const objectChoice = subitisingImageChoices[objectNumber];
    const object = subitisingImages[objectChoice].uri;

    return {
      answer, formation: perceptualFormation, timeout, colour: undefined, colour2: undefined, object, questionSelection: SubitisingCategoryName.OBJECT_FORMATION_1
    };
  }

  objectFormation2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 5) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 1;
    }

    // Pick a random perceptual formation for that number
    const perceptualFormations = perceptual_formations[answer];
    const pLen = perceptualFormations.length;
    // Generate random number between 1 and 5
    const pChoice = Math.floor(Math.random() * pLen);
    const perceptualFormation = perceptualFormations[pChoice];

    let timeout = TIMEOUT;
    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random object
    const objectNumber = Math.floor(Math.random() * subitisingImageChoices.length);
    const objectChoice = subitisingImageChoices[objectNumber];
    const object = subitisingImages[objectChoice].uri;

    return {
      answer, formation: perceptualFormation, timeout, colour: undefined, colour2: undefined, object, questionSelection: SubitisingCategoryName.OBJECT_FORMATION_1
    };
  }

  fiveFrame1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 0 and 5
    let answer = Math.floor(Math.random() * 6);

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 6);
    }

    const fiveFrameFormation = five_frame_formations[answer];

    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: fiveFrameFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.FIVE_FRAME_1
    };
  }

  fiveFrame2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 5
    let answer = Math.floor(Math.random() * 6);

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 6);
    }

    const fiveFrameFormation = five_frame_formations[answer];

    // Generate timeout amount
    let timeout = TIMEOUT;

    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    const questionAnswer = {
      answer, formation: fiveFrameFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.FIVE_FRAME_1
    };

    return questionAnswer;
  }

  tenFrame1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 0 and 10
    let answer = Math.floor(Math.random() * 11);

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 11);
    }

    const tenFrameFormations = ten_frame_formations[answer];
    const tFLen = tenFrameFormations.length;
    // Generate random number between 1 and 5
    const tFChoice = Math.floor(Math.random() * tFLen);
    const tenFrameFormation = tenFrameFormations[tFChoice];

    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: tenFrameFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.TEN_FRAME_1
    };
  }

  tenFrame2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 0 and 10
    let answer = Math.floor(Math.random() * 11);

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 11);
    }

    const tenFrameFormations = ten_frame_formations[answer];
    const tFLen = tenFrameFormations.length;
    // Generate random number between 1 and 5
    const tFChoice = Math.floor(Math.random() * tFLen);
    const tenFrameFormation = tenFrameFormations[tFChoice];

    // Generate timeout amount
    let timeout = TIMEOUT;

    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    const questionAnswer = {
      answer, formation: tenFrameFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.TEN_FRAME_1
    };

    return questionAnswer;
  }

  fiveToNine1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 5 and 9
    let answer = Math.floor(Math.random() * 5) + 5;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 5;
    }

    // Pick a random five to nine formation for that number
    const fiveToNineFormations = perceptual_formations[answer];
    const ftnLen = fiveToNineFormations.length;
    const ftnChoice = Math.floor(Math.random() * ftnLen);
    const fiveToNineFormation = fiveToNineFormations[ftnChoice];

    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: fiveToNineFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.FIVE_TO_NINE_1
    };
  }

  fiveToNine2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 5 and 9
    let answer = Math.floor(Math.random() * 5) + 5;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 5) + 5;
    }

    // Pick a random five to nine formation for that number
    const fiveToNineFormations = perceptual_formations[answer];
    const ftnLen = fiveToNineFormations.length;
    const ftnChoice = Math.floor(Math.random() * ftnLen);
    const fiveToNineFormation = fiveToNineFormations[ftnChoice];

    let timeout = TIMEOUT;
    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: fiveToNineFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.FIVE_TO_NINE_1
    };
  }

  oneToNine1(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 9
    let answer = Math.floor(Math.random() * 9) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 9) + 1;
    }

    // Pick a random zero to nine formation for that number
    const zeroToNineFormations = perceptual_formations[answer];
    const ztnLen = zeroToNineFormations.length;
    const ztnChoice = Math.floor(Math.random() * ztnLen);
    const zeroToNineFormation = zeroToNineFormations[ztnChoice];

    const timeout = 0;

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: zeroToNineFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.ONE_TO_NINE_1
    };
  }

  oneToNine2(previousQuestions, lastQuestions) {

    let lastAnswer = 0;
    const len = lastQuestions.length;
    if (len > 0) {
      lastAnswer = lastQuestions[len - 1].answer;
    }

    // Generate random number between 1 and 9
    let answer = Math.floor(Math.random() * 9) + 1;

    while (lastAnswer == answer) {
      answer = Math.floor(Math.random() * 9) + 1;
    }

    // Pick a random zero to nine formation for that number
    const zeroToNineFormations = perceptual_formations[answer];
    const ztnLen = zeroToNineFormations.length;
    const ztnChoice = Math.floor(Math.random() * ztnLen);
    const zeroToNineFormation = zeroToNineFormations[ztnChoice];

    let timeout = TIMEOUT;
    if (len > 0) {
      if (lastQuestions[len - 1].correct) {
        if (lastQuestions[len - 1].timeout > 400) {
          timeout = lastQuestions[len - 1].timeout - 200;
        } else {
          timeout = lastQuestions[len - 1].timeout;
        }
      } else {
        timeout = lastQuestions[len - 1].timeout + 200;
      }
    }

    // Pick random colour
    const colourNumber = Math.floor(Math.random() * numColours);
    const colour = colourArray[colourNumber];

    let colourNumber2 = Math.floor(Math.random() * numColours);
    while (colourNumber2 == colourNumber) {
      colourNumber2 = Math.floor(Math.random() * numColours);
    }
    const colour2 = colourArray[colourNumber];

    return {
      answer, formation: zeroToNineFormation, timeout, colour, colour2, object: undefined, questionSelection: SubitisingCategoryName.ONE_TO_NINE_1
    };
  }

  magicMix1(previousQuestions, lastQuestions) {
    // Magic Mix for subitising only selects 0 - 5

    let questionAnswer;

    // Select a type of question:
    // 0: Dice Formation
    // 1: Perceptual Formation
    // 2: Random Formation
    // 3: Two Colours
    // 4: Five Frame
    // 5: Objects

    const formationSelection = Math.floor(Math.random() * 6);

    switch (formationSelection) {
      case 0: // Dice Formation
      {
        questionAnswer = this.diceFormation1(previousQuestions, lastQuestions);
        break;
      }
      case 1: // Perceptual Formation
      {
        questionAnswer = this.perceptualFormation1(previousQuestions, lastQuestions);
        break;
      }
      case 2: // Random Formation
      {
        questionAnswer = this.randomFormation1(previousQuestions, lastQuestions);
        break;
      }
      case 3: // Two Colours
      {
        questionAnswer = this.twoColoursFormation1(previousQuestions, lastQuestions);
        break;
      }
      case 4:
      {
        questionAnswer = this.fiveFrame1(previousQuestions, lastQuestions);
        break;
      }
      case 5:
      {
        questionAnswer = this.objectFormation1(previousQuestions, lastQuestions);
        break;
      }
    }

    return questionAnswer;

  }

  magicMix2(previousQuestions, lastQuestions) {
    // Magic Mix for subitising only selects 0 - 5

    let questionAnswer;

    // Select a type of question:
    // 0: Dice Formation
    // 1: Perceptual Formation
    // 2: Random Formation
    // 3: Two Colours
    // 4: Five Frame
    // 5: Objects

    const formationSelection = Math.floor(Math.random() * 6);

    switch (formationSelection) {
      case 0: // Dice Formation
      {
        questionAnswer = this.diceFormation2(previousQuestions, lastQuestions);
        break;
      }
      case 1: // Perceptual Formation
      {
        questionAnswer = this.perceptualFormation2(previousQuestions, lastQuestions);
        break;
      }
      case 2: // Random Formation
      {
        questionAnswer = this.randomFormation2(previousQuestions, lastQuestions);
        break;
      }
      case 3: // Two Colours
      {
        questionAnswer = this.twoColoursFormation2(previousQuestions, lastQuestions);
        break;
      }
      case 4:
      {
        questionAnswer = this.fiveFrame2(previousQuestions, lastQuestions);
        break;
      }
      case 5:
      {
        questionAnswer = this.objectFormation2(previousQuestions, lastQuestions);
        break;
      }
    }

    return questionAnswer;

  }

}
